import React, { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import closeMediumTokens from '@amzn/meridian-tokens/base/icon/close-medium';
import { ProcessPathCard } from '../flowMap/model';
import { formatFlowNumber } from '../flowMap/utils';
import { getBuffersDetailsData } from './api';
import { ContainerType } from './constants';
import { ContainerDetail, GetBuffersDetailsDataOutput } from './model';
import {
    CONTAINER_STATUS_LABELS,
    CONTAINER_TYPE_FILTERS,
    CONTAINER_TYPE_LABELS,
    DWELL_BUCKETS,
    STATUS_INDEX_TEXT,
    bufferCardLabel,
    formatDwell,
    formatDwellDays,
    formatScanTime,
    sortByDwell,
} from './utils';

export interface BufferDetailsPanelProps {
    warehouseId: string;
    card: ProcessPathCard;
    onClose: () => void;
}

/** Id of the panel's heading; the selected buffer card points at it. */
export const BUFFER_DETAILS_HEADING_ID = 'buffer-details-heading';

/** Rows per page of the container table. */
export const CONTAINER_PAGE_SIZE = 100;

/** The sortable column of the container table. */
const DWELL_COLUMN = 'dwellHours';

type SortDirection = 'ascending' | 'descending';

/**
 * The drill-down for one buffer, opened from its card: header stats, the dwell distribution, and
 * every container in the buffer.
 *
 * The container table lists one container type at a time: the header's Pallets / Totes / Units
 * counts are links that pick it (units first), as GetBuffersDetailsData serves it with `containerType`: every
 * container of that type in the buffer, each naming its parent. Switching type refetches the
 * table and keeps the header up; the current rows stay in place until the new ones arrive, so the
 * page height doesn't collapse and scroll the reader back up. The table pages with "Load more". Pressing the Dwell Time
 * header sorts the rows loaded so far by dwell, longest first, then shortest first, then back to
 * the service's order. Once the drill-down first loads it scrolls the table into view. The page keys
 * the panel by buffer, so opening another buffer starts fresh.
 */
const BufferDetailsPanel: React.FC<BufferDetailsPanelProps> = ({ warehouseId, card, onClose }) => {
    const path = card.topologyNode?.processPath?.processPath;
    const processId = path?.processId ?? '';
    const functionId = path?.functionId ?? '';

    const [detail, setDetail] = useState<GetBuffersDetailsDataOutput | null>(null);
    const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [rows, setRows] = useState<ContainerDetail[]>([]);
    const [nextToken, setNextToken] = useState<string | undefined>();
    const [pageStatus, setPageStatus] = useState<'idle' | 'loading' | 'error'>('idle');
    const [sortDirection, setSortDirection] = useState<SortDirection | undefined>();
    const [containerType, setContainerType] = useState<ContainerType>(ContainerType.UNIT);
    // A type switch reloads the table only; the header stays up.
    const [tableStatus, setTableStatus] = useState<'ready' | 'loading' | 'error'>('ready');
    // Bumped by the table error's Retry: re-picking the same type would not change containerType.
    const [reload, setReload] = useState(0);
    // Whether the panel is still open: a page that lands after it closes is dropped.
    const mounted = useRef(false);
    // The latest open / type switch: a response for an earlier one is dropped.
    const latestOpen = useRef(0);
    // Whether the header has loaded: after that, a type switch reloads only the table.
    const headerLoaded = useRef(false);
    // The container table, scrolled into view once the drill-down first loads.
    const tableRef = useRef<HTMLDivElement>(null);
    const scrolledToTable = useRef(false);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    useEffect(() => {
        const requestId = ++latestOpen.current;
        const isLatest = () => mounted.current && latestOpen.current === requestId;
        setErrorMessage(null);
        setPageStatus('idle');
        // The first open shows the loader in place of everything; a type switch only the table's.
        if (headerLoaded.current) {
            setTableStatus('loading');
        } else {
            setStatus('loading');
        }
        getBuffersDetailsData({ warehouseId, processId, functionId, containerType, maxResults: CONTAINER_PAGE_SIZE })
            .then((output) => {
                if (!isLatest()) return;
                headerLoaded.current = true;
                setDetail(output);
                setRows(output.containers ?? []);
                setNextToken(output.nextToken);
                setStatus('ready');
                setTableStatus('ready');
            })
            .catch((error: Error) => {
                if (!isLatest()) return;
                const message = error?.message || 'Failed to load the buffer details.';
                setErrorMessage(message);
                if (headerLoaded.current) {
                    setTableStatus('error');
                } else {
                    setStatus('error');
                }
            });
    }, [warehouseId, processId, functionId, containerType, reload]);

    // Opening a card brings its containers into view, once: a type switch or a page of
    // "Load more" leaves the reader where they are.
    useEffect(() => {
        if (status !== 'ready' || scrolledToTable.current) return;
        scrolledToTable.current = true;
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        tableRef.current?.scrollIntoView?.({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }, [status]);

    /** Appends the next page of containers; dropped if a type switch has replaced the table since. */
    const loadMore = useCallback(() => {
        const requestId = latestOpen.current;
        const isLatest = () => mounted.current && latestOpen.current === requestId;
        setPageStatus('loading');
        getBuffersDetailsData({
            warehouseId,
            processId,
            functionId,
            containerType,
            maxResults: CONTAINER_PAGE_SIZE,
            nextToken,
        })
            .then((output) => {
                if (!isLatest()) return;
                setRows((previous) => previous.concat(output.containers ?? []));
                setNextToken(output.nextToken);
                setPageStatus('idle');
            })
            .catch(() => {
                if (!isLatest()) return;
                setPageStatus('error');
            });
    }, [warehouseId, processId, functionId, containerType, nextToken]);

    /** Cycles the Dwell Time sort: longest first, shortest first, then the service's order. */
    const onSort = () =>
        setSortDirection((previous) =>
            previous === undefined ? 'descending' : previous === 'descending' ? 'ascending' : undefined
        );

    const label = bufferCardLabel(card);
    const total = detail?.totalAtThisLevel ?? rows.length;
    const shown = sortDirection ? sortByDwell(rows, sortDirection) : rows;

    return (
        <Box type="outline" spacingInset="400" aria-label={`${label} buffer details`} data-testid="buffer-details">
            <Column spacing="400">
                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Heading level={3} type="h300">
                        <span id={BUFFER_DETAILS_HEADING_ID}>{label} — Buffer Details</span>
                    </Heading>
                    <Button
                        type="icon"
                        onClick={onClose}
                        aria-label="Close buffer details"
                        data-testid="buffer-details-close"
                    >
                        <Icon tokens={closeMediumTokens} />
                    </Button>
                </Row>

                {status === 'loading' && <Loader data-testid="buffer-details-loader" />}

                {status === 'error' && (
                    <Alert type="error" data-testid="buffer-details-error">
                        {errorMessage}
                    </Alert>
                )}

                {status === 'ready' && detail && (
                    <>
                        <Row spacing="500" wrap="down" data-testid="buffer-details-stats">
                            {CONTAINER_TYPE_FILTERS.map((filter) => {
                                const selected = filter.value === containerType;
                                return (
                                    <Button
                                        key={filter.value}
                                        type="link"
                                        onClick={() => setContainerType(filter.value)}
                                        aria-pressed={selected ? 'true' : 'false'}
                                        data-testid={`buffer-type-${filter.value}`}
                                    >
                                        <Text type="b300" color={selected ? 'primary' : 'secondary'}>
                                            {filter.label}:{' '}
                                            <strong>{formatFlowNumber(detail[filter.countKey] as number | undefined)}</strong>
                                        </Text>
                                    </Button>
                                );
                            })}
                            {/* The table stays put while a new type loads, so the page doesn't collapse and jump. */}
                            {tableStatus === 'loading' && <Loader size="small" data-testid="buffer-table-loader" />}
                            <Text type="b300">
                                Potential Lost: <strong>{formatFlowNumber(detail.potentiallyLostUnits)}</strong>
                            </Text>
                            <Text type="b300">Avg Dwell: <strong>{formatDwellDays(detail.avgDwellHours)}</strong></Text>
                        </Row>
                        <Row spacing="400" wrap="down" data-testid="buffer-details-dwell">
                            {DWELL_BUCKETS.map((bucket) => (
                                <Text key={bucket.key} type="b200">
                                    {bucket.label}: <strong>{formatFlowNumber(detail[bucket.key] as number | undefined)}</strong>
                                </Text>
                            ))}
                        </Row>

                        {/* One child of the Column, so the scroll target adds no extra gap. */}
                        <div ref={tableRef} data-testid="buffer-table-section">
                        {tableStatus === 'error' && (
                            <Row spacing="300" alignmentVertical="center">
                                <Alert type="error" size="small" data-testid="buffer-table-error">
                                    {errorMessage}
                                </Alert>
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() => setReload((previous) => previous + 1)}
                                    data-testid="buffer-table-retry"
                                >
                                    Retry
                                </Button>
                            </Row>
                        )}

                        {tableStatus !== 'error' && (
                        <Table
                            headerRows={1}
                            showDividers
                            spacing="small"
                            sortColumn={sortDirection ? DWELL_COLUMN : undefined}
                            sortDirection={sortDirection}
                            onSort={onSort}
                            data-testid="buffer-container-table"
                        >
                            <TableRow>
                                <TableCell header>Container ID</TableCell>
                                <TableCell header>Type</TableCell>
                                <TableCell header>Parent</TableCell>
                                <TableCell header sortColumn={DWELL_COLUMN} data-testid="buffer-sort-dwell">
                                    Dwell Time
                                </TableCell>
                                <TableCell header>Last Scanned By</TableCell>
                                <TableCell header>Last Scan Time</TableCell>
                                <TableCell header>Location</TableCell>
                                <TableCell header>Status</TableCell>
                            </TableRow>
                            {shown.map((container) => (
                                <TableRow
                                    key={container.containerId}
                                    data-testid={`buffer-container-${container.containerId}`}
                                >
                                    <TableCell>{container.containerId}</TableCell>
                                    <TableCell>
                                        {CONTAINER_TYPE_LABELS[container.containerType] ?? container.containerType}
                                    </TableCell>
                                    <TableCell>{container.parentContainerId ?? '—'}</TableCell>
                                    <TableCell>{formatDwell(container.dwellHours)}</TableCell>
                                    <TableCell>{container.lastScannedBy ?? '—'}</TableCell>
                                    <TableCell>{formatScanTime(container.lastScanTimeMs)}</TableCell>
                                    <TableCell>{container.location ?? '—'}</TableCell>
                                    <TableCell>
                                        {container.status
                                            ? CONTAINER_STATUS_LABELS[container.status] ?? container.status
                                            : '—'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </Table>
                        )}
                        </div>

                        {tableStatus === 'ready' && (nextToken || pageStatus === 'error') && (
                            <Row spacing="300" alignmentVertical="center">
                                {pageStatus === 'loading' ? (
                                    <Loader size="small" data-testid="buffer-load-more-loader" />
                                ) : (
                                    <Button type="link" size="small" onClick={loadMore} data-testid="buffer-load-more">
                                        {pageStatus === 'error' ? 'Retry' : 'Load more'} ({formatFlowNumber(rows.length)} of{' '}
                                        {formatFlowNumber(total)})
                                    </Button>
                                )}
                                {pageStatus === 'error' && (
                                    <Text type="b200" color="error" data-testid="buffer-load-more-error">
                                        Failed to load more containers.
                                    </Text>
                                )}
                            </Row>
                        )}

                        <Box backgroundColor="subdued" spacingInset="200 300" data-testid="buffer-status-index">
                            <Text type="b100">
                                <strong>Status Index:</strong> {STATUS_INDEX_TEXT}
                            </Text>
                        </Box>
                    </>
                )}
            </Column>
        </Box>
    );
};

export default BufferDetailsPanel;
